import { useMemo } from 'react';
import { addDays, format } from 'date-fns';
import { TZDate } from '@date-fns/tz';

import { TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';
import type { TourWebApiDto } from '@zg-rentals/ts-z-calendar-client';
import { useZCalendarWebApiHandler_getToursForListing } from '@zg-rentals/ts-z-calendar-client-react-query';

import { ZCALENDAR_TOURS_QUERY_TAG } from '../constants/queryTags';
import { convertDateToLocalDate, formatTourTimeRange } from '../utils/formatTime';
import { hideCancelledToursWithActiveTour } from '../utils/tourCalendarWebApiBuilder';

export const UPCOMING_TOURS_WINDOW_DAYS = 3;

const FETCHED_TOUR_STATUSES = [
  TourStatusForZcheduler.CANCELLED,
  TourStatusForZcheduler.CONFIRMED,
  TourStatusForZcheduler.REQUESTED,
];

const DATE_KEY_FORMAT = 'yyyy-MM-dd';

type DatedTour = TourWebApiDto & { dateKey: string };

export type UpcomingTourEvent = {
  conversationId: string;
  renterName: string;
  startMs: number;
  timeRange: string;
  tourId: string;
  tourStatus: TourStatusForZcheduler;
};

export type UpcomingToursDay = {
  date: Date;
  events: Array<UpcomingTourEvent>;
};

export type UpcomingTourCounts = {
  booked: number;
  canceled: number;
  requested: number;
};

type UseUpcomingToursDataOptions = {
  daysAhead?: number;
  enabled?: boolean;
};

// A tour's true instant: its wall-clock start interpreted in the listing's timezone.
const getTourStartInstant = ({ startDateTime, zoneId }: TourWebApiDto) =>
  new TZDate(
    startDateTime.date.year,
    startDateTime.date.month - 1,
    startDateTime.date.day,
    startDateTime.time.hour,
    startDateTime.time.minute,
    zoneId,
  ).getTime();

// Buckets by the viewer's calendar day of that instant, matching the previous widget's semantics.
// An unusable zoneId yields a NaN instant, so return undefined and let the caller drop the tour
// rather than letting `format` throw mid-render.
const getViewerDateKey = (tour: TourWebApiDto) => {
  const startInstant = getTourStartInstant(tour);
  return Number.isFinite(startInstant) ? format(new Date(startInstant), DATE_KEY_FORMAT) : undefined;
};

const toUpcomingTourEvent = (tour: TourWebApiDto): UpcomingTourEvent => ({
  conversationId: tour.conversationId,
  renterName: tour.bookedByName,
  startMs: getTourStartInstant(tour),
  timeRange: formatTourTimeRange(tour.startDateTime, tour.endDateTime),
  tourId: tour.id,
  tourStatus: tour.tourStatus,
});

const countByStatus = (events: Array<UpcomingTourEvent>, tourStatus: TourStatusForZcheduler) =>
  events.filter((event) => event.tourStatus === tourStatus).length;

/**
 * Data source for the redesigned Upcoming Tours widget: fetches requested, booked, and canceled
 * tours over the upcoming window and derives per-status counts plus per-day event buckets.
 * Canceled tours are hidden when the same renter already has an active tour, mirroring the
 * tour calendar's treatment; only tours inside the rendered window count as evidence of one.
 */
const useUpcomingToursData = (
  listingAlias: string,
  { daysAhead = UPCOMING_TOURS_WINDOW_DAYS, enabled = true }: UseUpcomingToursDataOptions = {},
) => {
  const today = useMemo(() => new Date(), []);

  const windowDates = useMemo(
    () => Array.from({ length: daysAhead }, (_, dayOffset) => addDays(today, dayOffset)),
    [today, daysAhead],
  );

  // The request overfetches one day on each side: the backend takes timezone-less LocalDates, so a
  // tour on the viewer's boundary days is guaranteed fetched regardless of whether the range is
  // interpreted on the viewer's or the listing's calendar. The window filter below discards the
  // excess before anything else reads it.
  const { data, isError, isLoading } = useZCalendarWebApiHandler_getToursForListing(
    {
      listingAlias,
      bookedByBrand: '',
      bookedByUserId: '',
      fromDate: convertDateToLocalDate(addDays(today, -1)),
      toDate: convertDateToLocalDate(addDays(today, daysAhead)),
      tourStatuses: FETCHED_TOUR_STATUSES,
    },
    {
      enabled: enabled && Boolean(listingAlias),
      meta: {
        tags: [ZCALENDAR_TOURS_QUERY_TAG],
        context: {
          api: 'getToursForListing',
          details: 'useUpcomingToursData - Error fetching upcoming tours',
        },
      },
    },
  );

  const { counts, days } = useMemo(() => {
    const windowKeys = windowDates.map((date) => format(date, DATE_KEY_FORMAT));
    const windowKeySet = new Set(windowKeys);

    // Trim to the rendered window before deduping: an overfetched rebooking the widget never shows
    // must not suppress a cancellation the widget does show.
    const inWindowTours = (data?.tours ?? []).flatMap((tour) => {
      const dateKey = getViewerDateKey(tour);
      return dateKey && windowKeySet.has(dateKey) ? [{ ...tour, dateKey }] : [];
    });

    const toursByDateKey = hideCancelledToursWithActiveTour(inWindowTours).reduce<Record<string, Array<DatedTour>>>(
      (acc, tour) => {
        (acc[tour.dateKey] ??= []).push(tour);
        return acc;
      },
      {},
    );

    const days = windowDates.map((date, dayIndex) => ({
      date,
      events: (toursByDateKey[windowKeys[dayIndex]] ?? [])
        .sort((a, b) => getTourStartInstant(a) - getTourStartInstant(b))
        .map(toUpcomingTourEvent),
    }));

    // Counts derive from the bucketed events so the tiles and the day list can never disagree
    // (overfetched tours outside the viewer's window are excluded from both).
    const bucketedEvents = days.flatMap(({ events }) => events);

    return {
      counts: {
        booked: countByStatus(bucketedEvents, TourStatusForZcheduler.CONFIRMED),
        canceled: countByStatus(bucketedEvents, TourStatusForZcheduler.CANCELLED),
        requested: countByStatus(bucketedEvents, TourStatusForZcheduler.REQUESTED),
      },
      days,
    };
  }, [data, windowDates]);

  return { counts, days, isError, isLoading };
};

export default useUpcomingToursData;
